import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Feather, Wind, Shield, Anchor, Heart, Zap, Waves, Cloud, Lock, Leaf } from 'lucide-react';
import { LettingGoRule } from '../types';

const RULES: LettingGoRule[] = [
  { id: 1, title: "Detachment is Freedom", concept: "Non-Attachment", description: "Detachment doesn't mean you don't own things; it means things don't own you. When you let go of the need to control, you gain the freedom to be.", icon: "Feather" },
  { id: 2, title: "Surrender to the Flow", concept: "Wu Wei", description: "Stop swimming against the current. Life has a natural flow. When you stop resisting what is, you conserve energy and find peace.", icon: "Waves" },
  { id: 3, title: "The Past is Heavy", concept: "Release", description: "Carrying anger or regret is like grasping a hot coal with the intent of throwing it at someone else; you are the one who gets burned.", icon: "Anchor" },
  { id: 4, title: "Acceptance is Not Resignation", concept: "Acceptance", description: "Acceptance is the active recognition of reality. Only when you accept the present moment can you change the next one.", icon: "Shield" },
  { id: 5, title: "Empty Your Boat", concept: "Ego", description: "If a man crosses a river and an empty boat collides with his own skiff, he will not be angry. If he sees a man in the boat, he will shout. Empty your ego, and no one can oppose you.", icon: "Cloud" },
  { id: 6, title: "Forgiveness is for You", concept: "Forgiveness", description: "Forgiveness is unlocking the door to let yourself out. It is not about condoning the other's action, but about releasing your own suffering.", icon: "Heart" },
  { id: 7, title: "Change is the Only Constant", concept: "Impermanence", description: "Everything changes. Holding on to how things used to be prevents you from enjoying how things are.", icon: "Wind" },
  { id: 8, title: "Trust the Unknown", concept: "Trust", description: "Anxiety comes from trying to predict the future. Letting go means trusting that you have the resilience to handle whatever comes.", icon: "Lock" },
  { id: 9, title: "Minimalism of the Mind", concept: "Simplicity", description: "Just as we declutter our homes, we must declutter our minds. Let go of thoughts that no longer serve you.", icon: "Leaf" },
  { id: 10, title: "Be Here Now", concept: "Presence", description: "You cannot let go of the past if you are not in the present. The only place where life exists is right here.", icon: "Zap" }
];

const IconMap: Record<string, React.ReactNode> = {
  Feather: <Feather size={24} />,
  Waves: <Waves size={24} />,
  Anchor: <Anchor size={24} />,
  Shield: <Shield size={24} />,
  Cloud: <Cloud size={24} />,
  Heart: <Heart size={24} />,
  Wind: <Wind size={24} />,
  Lock: <Lock size={24} />,
  Leaf: <Leaf size={24} />,
  Zap: <Zap size={24} />,
};

const LettingGo: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-indigo-50 text-indigo-600 rounded-full mb-4">
          <Feather size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">The Power of Letting Go</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"Surrender to what is. Let go of what was."</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          The art of detachment is not about indifference, but about emotional freedom. 
          Learn to release the heavy weights you carry.
        </p>
      </header>

      <div className="space-y-4">
        {RULES.map((rule) => (
          <div 
            key={rule.id} 
            className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-indigo-300 shadow-md' : 'border-nunchi-200 hover:border-indigo-200'}`}
          >
            <button 
              onClick={() => toggle(rule.id)}
              className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-50 text-gray-400'}`}>
                  {IconMap[rule.icon]}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                    {rule.title}
                  </h3>
                  <p className="text-xs text-nunchi-500 font-serif uppercase tracking-wider">{rule.concept}</p>
                </div>
              </div>
              <div className="text-nunchi-400">
                {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>
            
            <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
              <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-indigo-100 mt-2">
                {rule.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LettingGo;